import React, { useState } from 'react';
import { patientJourneys } from '../data/patientJourneys';
import { DomainsWheel } from './DomainsWheel';

// Sample survey questions tied to each patient's journey
const sampleQuestions = {
  maya: {
    domain: 'Research & Clinical Trial Participation',
    question: 'What motivated you to participate in a clinical trial, and how did that experience change your perspective on your condition?',
  },
  marcus: {
    domain: 'Identity, Background & Culture',
    question: 'How has your family history with this condition shaped the way you approach your own health journey?',
  },
  sarah: {
    domain: 'Support & Resources',
    question: 'What resources or support systems have been most valuable in your recovery, and what do you wish you had known earlier?',
  },
  james: {
    domain: 'Treatment Experience',
    question: 'Can you describe a treatment that finally worked for you, and what the journey to finding it was like?',
  },
  elena: {
    domain: 'Research & Clinical Trial Participation',
    question: 'After 30 years of experience, what wisdom would you share with someone newly diagnosed?',
  },
};

/**
 * PatientJourneyExperience - Interactive patient story timeline experience
 * Inspired by Petty Ranch Inn family archive
 */
export const PatientJourneyExperience = ({ onClose }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
  };

  const handleBack = () => {
    setSelectedPatient(null);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.closeButton} onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {!selectedPatient ? (
        /* Landing View - Patient Grid */
        <div style={styles.landingView}>
          <div style={styles.heroSection}>
            <h1 style={styles.headline}>Every patient has a story</h1>
            <p style={styles.subheadline}>
              We listen across 13 dimensions of health experience—from diagnosis to daily life,
              from treatment to emotional well-being. Here are a few of the journeys that inspire our work.
            </p>
            <p style={styles.disclaimer}>
              These stories are fictional composites for illustrative purposes only.
              We never share personally identifiable information—your privacy is always protected.
            </p>
          </div>

          {/* 13-Domains Wheel */}
          <div style={styles.wheelSection}>
            <DomainsWheel size={320} interactive={true} />
          </div>

          <div style={styles.patientGrid}>
            {patientJourneys.map((patient) => (
              <div
                key={patient.id}
                style={{
                  ...styles.patientCard,
                  '--patient-color': patient.color,
                }}
                onClick={() => handlePatientClick(patient)}
              >
                {/* Illustration or placeholder */}
                {patient.illustration ? (
                  <img
                    src={patient.illustration}
                    alt={patient.name}
                    style={styles.illustrationImage}
                  />
                ) : (
                  <div style={{ ...styles.illustrationPlaceholder, backgroundColor: patient.color }}>
                    <span style={styles.illustrationInitial}>{patient.name[0]}</span>
                  </div>
                )}
                <h3 style={styles.patientName}>{patient.name}</h3>
                <p style={styles.patientCondition}>{patient.condition}</p>
                <p style={styles.patientTeaser}>"{patient.teaser}"</p>
                <span style={styles.viewStory}>View journey →</span>
              </div>
            ))}
          </div>

          <div style={styles.ctaSection}>
            <p style={styles.ctaText}>Your story matters too.</p>
            <a href="https://apply.savvy.coop/register" style={styles.ctaButton}>Share Your Journey →</a>
          </div>
        </div>
      ) : (
        /* Journey View - Timeline */
        <div style={styles.journeyView}>
          <button style={styles.backButton} onClick={handleBack}>
            ← Back to all stories
          </button>

          <div style={styles.journeyHeader}>
            {/* Illustration or placeholder */}
            {selectedPatient.illustration ? (
              <img
                src={selectedPatient.illustration}
                alt={selectedPatient.name}
                style={styles.journeyIllustrationImage}
              />
            ) : (
              <div style={{ ...styles.journeyIllustration, backgroundColor: selectedPatient.color }}>
                <span style={styles.journeyInitial}>{selectedPatient.name[0]}</span>
              </div>
            )}
            <div style={styles.journeyInfo}>
              <h1 style={styles.journeyName}>{selectedPatient.name}</h1>
              <p style={styles.journeyMeta}>
                {selectedPatient.age} · {selectedPatient.condition} · {selectedPatient.location}
              </p>
              <p style={styles.journeyIntro}>{selectedPatient.fullIntro}</p>
            </div>
          </div>

          <div style={styles.timeline}>
            <div style={styles.timelineLine} />
            {selectedPatient.timeline.map((moment, index) => (
              <div key={index} style={styles.timelineMoment}>
                <div style={styles.timelineYear}>
                  <span style={{ ...styles.yearBadge, backgroundColor: selectedPatient.color }}>
                    {moment.year}
                  </span>
                </div>
                <div style={styles.timelineContent}>
                  <div style={styles.domainBadge}>{moment.domain}</div>
                  <h3 style={styles.momentTitle}>{moment.title}</h3>
                  <p style={styles.momentQuote}>"{moment.quote}"</p>
                </div>
              </div>
            ))}
          </div>

          {/* Survey Question Preview */}
          <div style={styles.surveyPreviewSection}>
            <p style={styles.surveyPreviewIntro}>
              This is how we'd ask about moments like {selectedPatient.name}'s:
            </p>

            {/* Mini survey card */}
            <div style={styles.surveyCard}>
              <div style={styles.surveyCardHeader}>
                <div style={{ ...styles.surveyDomainPill, backgroundColor: selectedPatient.color }}>
                  {sampleQuestions[selectedPatient.id]?.domain || 'Your Experience'}
                </div>
              </div>

              <p style={styles.surveyQuestion}>
                {sampleQuestions[selectedPatient.id]?.question || 'Tell us about a key moment in your health journey.'}
              </p>

              <div style={styles.surveyTextarea}>
                <span style={styles.surveyPlaceholder}>Share your experience in your own words...</span>
                <div style={styles.surveyTextareaCursor} />
              </div>

              <div style={styles.surveyCardFooter}>
                <span style={styles.surveyOptional}>Optional</span>
                <span style={styles.surveyCharCount}>0 / 2000</span>
              </div>
            </div>
          </div>

          <div style={styles.journeyCta}>
            <p style={styles.journeyCtaText}>
              {selectedPatient.name}'s story spans {selectedPatient.timeline[selectedPatient.timeline.length - 1].year - selectedPatient.timeline[0].year} years and {selectedPatient.timeline.length} key moments.
              <br />What's yours?
            </p>
            <a href="https://apply.savvy.coop/register" style={styles.ctaButton}>Share Your Story →</a>
          </div>
        </div>
      )}

      <style>{`
        .patient-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
        }
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f7f5f3',
    zIndex: 1000,
    overflow: 'auto',
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px 40px',
    backgroundColor: '#f7f5f3',
    zIndex: 10,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#272726',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  },

  // Landing View
  landingView: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 40px 80px',
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  headline: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: 'clamp(36px, 5vw, 56px)',
    fontWeight: 700,
    color: '#272726',
    marginBottom: '20px',
  },
  subheadline: {
    fontSize: '18px',
    lineHeight: 1.6,
    color: '#666',
    maxWidth: '600px',
    margin: '0 auto',
  },
  disclaimer: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#999',
    maxWidth: '500px',
    margin: '20px auto 0',
    fontStyle: 'italic',
  },
  wheelSection: {
    marginBottom: '60px',
    padding: '40px 0',
  },

  // Patient Grid
  patientGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '32px',
    marginBottom: '60px',
  },
  patientCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '32px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    textAlign: 'center',
  },
  illustrationImage: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    margin: '0 auto 20px',
    objectFit: 'cover',
    border: '3px solid rgba(0,0,0,0.08)',
  },
  illustrationPlaceholder: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    margin: '0 auto 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '3px solid rgba(0,0,0,0.1)',
  },
  illustrationInitial: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '48px',
    fontWeight: 700,
    color: 'rgba(0,0,0,0.3)',
  },
  patientName: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '24px',
    fontWeight: 700,
    color: '#272726',
    marginBottom: '4px',
  },
  patientCondition: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '16px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  patientTeaser: {
    fontSize: '16px',
    lineHeight: 1.5,
    color: '#555',
    fontStyle: 'italic',
    marginBottom: '20px',
  },
  viewStory: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#272726',
  },

  // CTA Section
  ctaSection: {
    textAlign: 'center',
    padding: '60px 0',
    borderTop: '1px solid rgba(0,0,0,0.08)',
  },
  ctaText: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '28px',
    fontWeight: 600,
    color: '#272726',
    marginBottom: '24px',
  },
  ctaButton: {
    display: 'inline-block',
    backgroundColor: '#272726',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    padding: '16px 32px',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
  },

  // Journey View
  journeyView: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 40px 80px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#666',
    cursor: 'pointer',
    padding: '8px 0',
    marginBottom: '40px',
  },
  journeyHeader: {
    display: 'flex',
    gap: '32px',
    alignItems: 'flex-start',
    marginBottom: '60px',
    flexWrap: 'wrap',
  },
  journeyIllustrationImage: {
    width: '160px',
    height: '160px',
    borderRadius: '50%',
    flexShrink: 0,
    objectFit: 'cover',
    border: '4px solid rgba(0,0,0,0.08)',
  },
  journeyIllustration: {
    width: '160px',
    height: '160px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '4px solid rgba(0,0,0,0.1)',
  },
  journeyInitial: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '64px',
    fontWeight: 700,
    color: 'rgba(0,0,0,0.3)',
  },
  journeyInfo: {
    flex: 1,
    minWidth: '280px',
  },
  journeyName: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '40px',
    fontWeight: 700,
    color: '#272726',
    marginBottom: '8px',
  },
  journeyMeta: {
    fontSize: '16px',
    color: '#888',
    marginBottom: '20px',
  },
  journeyIntro: {
    fontSize: '18px',
    lineHeight: 1.6,
    color: '#555',
  },

  // Timeline
  timeline: {
    position: 'relative',
    paddingLeft: '40px',
  },
  timelineLine: {
    position: 'absolute',
    left: '8px',
    top: '24px',
    bottom: '24px',
    width: '2px',
    backgroundColor: 'rgba(0,0,0,0.1)',
  },
  timelineMoment: {
    display: 'flex',
    gap: '24px',
    marginBottom: '48px',
    position: 'relative',
  },
  timelineYear: {
    position: 'absolute',
    left: '-40px',
    top: '0',
  },
  yearBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
  },
  timelineContent: {
    flex: 1,
    paddingLeft: '24px',
  },
  domainBadge: {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
  },
  momentTitle: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '22px',
    fontWeight: 600,
    color: '#272726',
    marginBottom: '12px',
  },
  momentQuote: {
    fontSize: '17px',
    lineHeight: 1.7,
    color: '#555',
    fontStyle: 'italic',
  },

  // Survey Preview
  surveyPreviewSection: {
    marginTop: '60px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  surveyPreviewIntro: {
    fontSize: '16px',
    color: '#888',
    marginBottom: '24px',
  },
  surveyCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '28px 32px',
    maxWidth: '480px',
    margin: '0 auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
    textAlign: 'left',
    border: '1px solid rgba(0,0,0,0.06)',
  },
  surveyCardHeader: {
    marginBottom: '16px',
  },
  surveyDomainPill: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  surveyQuestion: {
    fontFamily: '"Source Serif Pro", Georgia, serif',
    fontSize: '18px',
    fontWeight: 600,
    color: '#272726',
    lineHeight: 1.5,
    marginBottom: '20px',
  },
  surveyTextarea: {
    backgroundColor: '#f7f5f3',
    borderRadius: '10px',
    padding: '16px',
    minHeight: '100px',
    position: 'relative',
    border: '1px solid rgba(0,0,0,0.08)',
  },
  surveyPlaceholder: {
    color: '#999',
    fontSize: '15px',
    fontStyle: 'italic',
  },
  surveyTextareaCursor: {
    position: 'absolute',
    left: '16px',
    top: '42px',
    width: '2px',
    height: '18px',
    backgroundColor: '#272726',
    animation: 'blink 1s infinite',
  },
  surveyCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  surveyOptional: {
    fontSize: '12px',
    color: '#888',
    fontStyle: 'italic',
  },
  surveyCharCount: {
    fontSize: '12px',
    color: '#aaa',
  },

  // Journey CTA
  journeyCta: {
    textAlign: 'center',
    padding: '60px 0',
    marginTop: '40px',
    borderTop: '1px solid rgba(0,0,0,0.08)',
  },
  journeyCtaText: {
    fontSize: '20px',
    lineHeight: 1.6,
    color: '#555',
    marginBottom: '24px',
  },
};

export default PatientJourneyExperience;
